/**
 * "Library" tab panel of VideoSelectorModal: CenterStage search box and
 * results grid. Purely presentational — all state lives in the modal.
 */
import type { CenterstageVideo } from '../api/centerstageApi';
import { formatDuration } from '../utils/formatDuration';
import type { VideoSelectorModalStyles } from './videoSelectorModalStyles';

interface VideoSelectorLibraryTabProps {
  query: string;
  onQueryChange: (value: string) => void;
  results: CenterstageVideo[];
  isSearching: boolean;
  searchError: string | null;
  hasSearched: boolean;
  selectedLibraryVideo: CenterstageVideo | null;
  hasExplicitlyDeselected: boolean;
  currentVideoCenterstageId?: string;
  onToggleVideo: (video: CenterstageVideo, isSelected: boolean) => void;
  styles: VideoSelectorModalStyles;
}

export function VideoSelectorLibraryTab({
  query,
  onQueryChange,
  results,
  isSearching,
  searchError,
  hasSearched,
  selectedLibraryVideo,
  hasExplicitlyDeselected,
  currentVideoCenterstageId,
  onToggleVideo,
  styles,
}: VideoSelectorLibraryTabProps) {
  return (
    <div style={styles.libraryPanel}>
      {/* Search */}
      <div style={styles.searchWrapper}>
        <svg
          style={styles.searchIcon}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search Centerstage Video Library…"
          style={styles.searchInput}
          autoFocus
        />
        {isSearching && <span style={styles.searchSpinner}>⏳</span>}
      </div>

      {searchError && <div style={styles.errorBanner}>{searchError}</div>}

      {/* Results grid */}
      <div style={styles.resultsArea}>
        {results.length > 0 ? (
          <div style={styles.grid}>
            {results.map((video) => {
              const isExplicitlySelected = selectedLibraryVideo?.id === video.id;
              const isCurrentVideo = !hasExplicitlyDeselected && !selectedLibraryVideo && currentVideoCenterstageId === video.id;
              const isSelected = isExplicitlySelected || isCurrentVideo;
              const handleToggle = () => onToggleVideo(video, isSelected);
              return (
                <div
                  key={video.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={handleToggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle();
                    }
                  }}
                  style={{
                    ...styles.videoCard,
                    ...(isSelected ? styles.videoCardSelected : {}),
                  }}
                >
                  <div style={styles.videoCardThumbnail}>
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        style={styles.videoCardImage}
                        crossOrigin="anonymous"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={styles.videoCardNoThumb}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      </div>
                    )}
                    {video.duration !== undefined && (
                      <span style={styles.videoDurationBadge}>
                        {formatDuration(video.duration)}
                      </span>
                    )}
                    {isSelected && (
                      <div style={styles.videoSelectedBadge}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div style={styles.videoCardContent}>
                    <span style={styles.videoCardTitle}>{video.title}</span>
                    <span style={styles.videoCardId}>#{video.id}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={styles.emptyState}>
            {hasSearched && !isSearching ? (
              <p style={styles.emptyStateText}>
                No videos found matching &ldquo;{query}&rdquo;
              </p>
            ) : (
              <p style={styles.emptyStateText}>
                Type to search for videos
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
